import axios from "axios"

// Restaurant API Configuration
// Restaurant API Configuration
// Automatically use localhost server when running on localhost
const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
const RESTAURANT_API_BASE_URL = isLocalhost
  ? "http://localhost:5001/api"
  : (process.env.REACT_APP_API_URL || "https://server.turbo.sd/api")

// Restaurant API Client (separate from admin)
const restaurantApiClient = axios.create({
  baseURL: RESTAURANT_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Add restaurant auth token to requests
restaurantApiClient.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("restaurantToken")
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Add response interceptor for error handling
restaurantApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response

      switch (status) {
        case 401:
          // Unauthorized - only redirect if not already on login page
          if (!window.location.pathname.includes('/restaurant/login') && !window.location.pathname.includes('/restaurant/staff/login')) {
            window.dispatchEvent(new CustomEvent('session-expired', { detail: { type: 'restaurant' } }))
          }
          break
        case 403:
          // Forbidden
          console.error("Access forbidden:", data?.message || "Insufficient permissions")
          if (data?.message === "Invalid or expired token" || data?.message === "Access token required" || data?.message === "Invalid token or user inactive" || data?.message?.includes("expired")) {
            if (!window.location.pathname.includes('/restaurant/login') && !window.location.pathname.includes('/restaurant/staff/login')) {
              window.dispatchEvent(new CustomEvent('session-expired', { detail: { type: 'restaurant' } }))
            }
          }
          break
        case 404:
          // Not found
          console.error("Resource not found:", data?.message || "Requested resource not found")
          break
        case 422:
          // Validation error
          console.error("Validation error:", data?.message || "Invalid data provided")
          break
        case 500:
          // Server error
          console.error("Server error:", data?.message || "Internal server error")
          break
        default:
          console.error("API error:", data?.message || `Request failed with status ${status}`)
      }

      // Enhance error object with user-friendly message
      error.userMessage = data?.message || getDefaultErrorMessage(status)
    } else if (error.request) {
      // Network error
      console.error("Network error:", error.message)
      error.userMessage = "Network connection failed. Please check your internet connection and try again."
    } else {
      // Other error
      console.error("Request error:", error.message)
      error.userMessage = "An unexpected error occurred. Please try again."
    }

    return Promise.reject(error)
  }
)

// Helper function for default error messages
function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case 400: return "Invalid request. Please check your input and try again."
    case 401: return "Session expired. Please log in again."
    case 403: return "You don't have permission to perform this action."
    case 404: return "The requested resource was not found."
    case 422: return "Please check your input and try again."
    case 429: return "Too many requests. Please wait a moment and try again."
    case 500: return "Server error. Please try again later."
    case 503: return "Service temporarily unavailable. Please try again later."
    default: return "An error occurred. Please try again."
  }
}

// Restaurant Auth API (using restaurant client)
export const restaurantAuthAPI = {
  register: (data: any) => restaurantApiClient.post("/restaurant-auth/register", data),

  registerRestaurant: (data: any) => restaurantApiClient.post("/restaurant-auth/register-restaurant", data),

  login: (data: any) => restaurantApiClient.post("/restaurant-auth/login", data),

  getProfile: () => restaurantApiClient.get("/restaurant-auth/profile"),

  updateProfile: (data: any) => restaurantApiClient.put("/restaurant-auth/profile", data),

  changePassword: (data: any) => restaurantApiClient.put("/restaurant-auth/change-password", data),

  verifyEmail: (token: string) => restaurantApiClient.post("/restaurant-auth/verify-email", { token }),
}

// Restaurant Portal API (using restaurant client)
export const restaurantPortalAPI = {
  getDashboard: (restaurantId: string) => restaurantApiClient.get(`/restaurant-portal/dashboard/${restaurantId}`),

  getFoodItems: (restaurantId: string, params?: any) =>
    restaurantApiClient.get(`/restaurant-portal/food-items/${restaurantId}`, { params }),

  getFoodItem: (itemId: string) => restaurantApiClient.get(`/food-items/${itemId}`),

  createFoodItem: (data: any) => restaurantApiClient.post("/restaurant-portal/food-items", data),

  updateFoodItem: (itemId: string, data: any) =>
    restaurantApiClient.put(`/restaurant-portal/food-items/${itemId}`, data),

  updateFoodItemAvailability: (itemId: string, data: { isAvailable: boolean }) =>
    restaurantApiClient.patch(`/restaurant-portal/food-items/${itemId}/availability`, data),

  deleteFoodItem: (itemId: string) => restaurantApiClient.delete(`/restaurant-portal/food-items/${itemId}`),

  getTables: (restaurantId: string) => restaurantApiClient.get(`/restaurant-portal/tables/${restaurantId}`),

  createTable: (data: any) => restaurantApiClient.post("/restaurant-portal/tables", data),

  updateTableStatus: (tableId: string, data: { status: string }) =>
    restaurantApiClient.patch(`/restaurant-portal/tables/${tableId}/status`, data),

  deleteTable: (tableId: string) => restaurantApiClient.delete(`/restaurant-portal/tables/${tableId}`),

  getTableOrders: (tableId: string) => restaurantApiClient.get(`/restaurant-portal/tables/${tableId}/orders`),

  closeTable: (tableId: string, data: any) => restaurantApiClient.post(`/restaurant-portal/tables/${tableId}/close`, data),

  moveTableOrders: (tableId: string, data: any) => restaurantApiClient.post(`/restaurant-portal/tables/${tableId}/move`, data),

  getOrders: (restaurantId: string, params?: any) =>
    restaurantApiClient.get(`/restaurant-portal/orders/${restaurantId}`, { params }),

  updateOrderStatus: (orderId: string, data: { status: string; note?: string }) =>
    restaurantApiClient.patch(`/restaurant-portal/orders/${orderId}/status`, data),

  downloadQRCode: (tableId: string) =>
    restaurantApiClient.get(`/restaurant-portal/tables/${tableId}/qr-code`, { responseType: 'arraybuffer' }),

  getRestaurantInfo: (restaurantId: string) => restaurantApiClient.get(`/restaurants/${restaurantId}`),

  updateRestaurantInfo: (restaurantId: string, data: any) =>
    restaurantApiClient.put(`/restaurants/${restaurantId}`, data),

  getRestaurantFoodItems: (restaurantId: string, params?: any) =>
    restaurantApiClient.get(`/restaurant-portal/food-items/${restaurantId}`, { params }),

  // Analytics and Reports APIs
  getAnalytics: (restaurantId: string, period?: string) =>
    restaurantApiClient.get(`/restaurant-portal/analytics/${restaurantId}`, {
      params: { period: period || "30d" }
    }),

  getFoodItemsPerformance: (restaurantId: string, period?: string) =>
    restaurantApiClient.get(`/restaurant-portal/food-items-performance/${restaurantId}`, {
      params: { period: period || "30d" }
    }),

  getReports: (restaurantId: string, type?: string, date?: string) =>
    restaurantApiClient.get(`/restaurant-portal/reports/${restaurantId}`, {
      params: {
        type: type || "monthly",
        date: date || new Date().toISOString()
      }
    }),

  getDailySalesReport: (restaurantId: string, date?: string) =>
    restaurantApiClient.get(`/reports/${restaurantId}/daily-sales`, { params: { date } }),

  getVoidsCompsReport: (restaurantId: string, startDate?: string, endDate?: string) =>
    restaurantApiClient.get(`/reports/${restaurantId}/voids-comps`, { params: { startDate, endDate } }),

  getMenuPopularityReport: (restaurantId: string, startDate?: string, endDate?: string) =>
    restaurantApiClient.get(`/reports/${restaurantId}/menu-popularity`, { params: { startDate, endDate } }),

  getRealtimeStats: (restaurantId: string) =>
    restaurantApiClient.get(`/restaurant-portal/realtime-stats/${restaurantId}`),

  // POS APIs
  getPOSDashboard: (restaurantId: string) =>
    restaurantApiClient.get(`/pos/dashboard/${restaurantId}`),

  getPOSOrders: (restaurantId: string, params?: any) =>
    restaurantApiClient.get(`/pos/orders/${restaurantId}`, { params }),

  createPOSOrder: (data: any) =>
    restaurantApiClient.post(`/pos/orders`, data),

  updatePOSOrderStatus: (orderId: string, data: any) =>
    restaurantApiClient.patch(`/pos/orders/${orderId}/status`, data),

  processPOSPayment: (orderId: string, data: any) =>
    restaurantApiClient.patch(`/pos/orders/${orderId}/payment`, data),

  processPOSRefund: (orderId: string, data: any) =>
    restaurantApiClient.patch(`/pos/orders/${orderId}/refund`, data),

  getPOSOrderDetails: (orderId: string) =>
    restaurantApiClient.get(`/pos/orders/${orderId}/details`),

  getPOSAnalytics: (restaurantId: string, period?: string) =>
    restaurantApiClient.get(`/pos/analytics/${restaurantId}`, {
      params: { period: period || "7d" }
    }),

  // Restaurant Users APIs
  loginStaff: (data: { email: string; password: string; restaurantId?: string }) =>
    restaurantApiClient.post("/restaurant-users/login", data),

  getRestaurantUsers: (restaurantId: string, params?: any) =>
    restaurantApiClient.get(`/restaurant-users/${restaurantId}`, { params }),

  createRestaurantUser: (data: any) =>
    restaurantApiClient.post(`/restaurant-users`, data),

  getRestaurantUser: (userId: string) =>
    restaurantApiClient.get(`/restaurant-users/user/${userId}`),

  updateRestaurantUser: (userId: string, data: any) =>
    restaurantApiClient.put(`/restaurant-users/user/${userId}`, data),

  updateUserPassword: (userId: string, data: any) =>
    restaurantApiClient.patch(`/restaurant-users/user/${userId}/password`, data),

  updateUserPermissions: (userId: string, data: any) =>
    restaurantApiClient.patch(`/restaurant-users/user/${userId}/permissions`, data),

  toggleUserStatus: (userId: string, data: any) =>
    restaurantApiClient.patch(`/restaurant-users/user/${userId}/status`, data),

  deleteRestaurantUser: (userId: string) =>
    restaurantApiClient.delete(`/restaurant-users/user/${userId}`),

  getUserStats: (userId: string, period?: string) =>
    restaurantApiClient.get(`/restaurant-users/user/${userId}/stats`, {
      params: { period: period || "30d" }
    }),

  // Payment Methods APIs
  getRestaurantPaymentMethods: (restaurantId: string) =>
    restaurantApiClient.get(`/restaurant-portal/payment-methods/${restaurantId}`),

  updateRestaurantPaymentMethods: (restaurantId: string, data: any) =>
    restaurantApiClient.put(`/restaurant-portal/payment-methods/${restaurantId}`, data),

  enableRestaurantPaymentMethod: (restaurantId: string, paymentMethodId: string) =>
    restaurantApiClient.patch(`/restaurant-portal/payment-methods/${restaurantId}/${paymentMethodId}/toggle`),

  disableRestaurantPaymentMethod: (restaurantId: string, paymentMethodId: string) =>
    restaurantApiClient.patch(`/restaurant-portal/payment-methods/${restaurantId}/${paymentMethodId}/toggle`),

  setDefaultPaymentMethod: (restaurantId: string, paymentMethodId: string) =>
    restaurantApiClient.patch(`/restaurant-portal/payment-methods/${restaurantId}/${paymentMethodId}/default`),

  updatePaymentMethodCustomFee: (restaurantId: string, paymentMethodId: string, data: any) =>
    restaurantApiClient.patch(`/restaurant-portal/payment-methods/${restaurantId}/${paymentMethodId}/custom-fee`, data),

  // Restaurant Settings APIs
  getRestaurantSettings: (restaurantId: string) =>
    restaurantApiClient.get(`/restaurant-portal/settings/${restaurantId}`),

  updateRestaurantSettings: (restaurantId: string, data: any) =>
    restaurantApiClient.put(`/restaurant-portal/settings/${restaurantId}`, data),
}

export default restaurantApiClient 